import { useAtom } from "jotai";
import { participantsAtom } from "../state/party";
import type { Participant } from "@pineseed/gui/types";

/**
 * useMessageParticipants - Hook for getting participant info for messages
 *
 * ARCHITECTURE COMPLIANCE:
 * - Pure UI hook - bridges state atoms only
 * - No business logic, no external calls
 * - Follows proper layer separation
 */
export function useMessageParticipants() {
  const [participants] = useAtom(participantsAtom);

  // Create a lookup function for participant info by participantId (email)
  const getParticipantInfo = (
    participantId: string | null | undefined,
  ): Participant | null => {
    if (!participantId) return null;
    return participants.find((p) => p.participantId === participantId) || null;
  };

  // Helper function to get display info for a message
  const getMessageDisplayInfo = (
    participantId: string | null | undefined,
    messageRole: "user" | "assistant" | "system",
  ) => {
    const participant = getParticipantInfo(participantId);

    if (participant) {
      return {
        name: participant.name,
        avatar: participant.avatar,
        fallback: participant.name.charAt(0).toUpperCase(),
        isBot: participant.type === "bot",
        role: participant.role,
        status: participant.status,
      };
    }

    // Fallback for messages without participant info
    if (messageRole === "assistant") {
      return {
        name: "AI Assistant",
        avatar: "",
        fallback: "AI",
        isBot: true,
        role: "assistant",
        status: "online" as const,
      };
    }

    if (messageRole === "system") {
      return {
        name: "System",
        avatar: "",
        fallback: "SYS",
        isBot: true,
        role: "system",
        status: "online" as const,
      };
    }

    return {
      name: "User",
      avatar: "",
      fallback: "U",
      isBot: false,
      role: "user",
      status: "online" as const,
    };
  };

  return {
    participants,
    getParticipantInfo,
    getMessageDisplayInfo,
    participantCount: participants.length,
  };
}
